import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';

const ADMIN_EMAILS = [
  'mjonthemove@gmail.com',
];

const PAGE_SIZE = 20;

export default function Admin() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [users,    setUsers]    = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(0);
  const [total,    setTotal]    = useState(0);

  useEffect(() => {
    if (user && !ADMIN_EMAILS.includes(user.email)) navigate('/feed');
  }, [user, navigate]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  async function fetchStats() {
    const { count: totalUsers }   = await supabase.from('profiles').select('*', { count: 'exact', head: true });
    const { count: approvedUsers }= await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('photo_approved', true);
    const { count: pendingUsers } = await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('photo_approved', false).not('profile_photo_url', 'is', null);
    const { count: totalRatings } = await supabase.from('ratings').select('*', { count: 'exact', head: true });
    const { count: noPhoto }      = await supabase.from('profiles').select('*', { count: 'exact', head: true }).is('profile_photo_url', null);
    setStats({ totalUsers, approvedUsers, pendingUsers, totalRatings, noPhoto });
  }

  async function fetchUsers() {
    setLoading(true);
    let query = supabase
      .from('profiles')
      .select('id, display_name, profile_photo_url, photo_approved, current_score, total_ratings_received, created_at, date_of_birth', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (search.trim()) {
      query = query.ilike('display_name', `%${search.trim()}%`);
    }

    const { data, count } = await query;
    setUsers(data || []);
    setTotal(count || 0);
    setLoading(false);
  }

  async function toggleApproval(id, current) {
    await supabase.from('profiles').update({ photo_approved: !current }).eq('id', id);
    setUsers(u => u.map(user => user.id === id ? { ...user, photo_approved: !current } : user));
    fetchStats();
  }

  async function deleteUser(id, name) {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    await supabase.from('ratings').delete().eq('rater_user_id', id);
    await supabase.from('ratings').delete().eq('rated_user_id', id);
    await supabase.from('profiles').delete().eq('id', id);
    setUsers(u => u.filter(user => user.id !== id));
    fetchStats();
  }

  function getAge(dob) {
    if (!dob) return '—';
    return `${Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000))}`;
  }

  function formatDate(ts) {
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  if (!user || !ADMIN_EMAILS.includes(user.email)) return null;

  return (
    <div className="page" style={{ paddingTop: 80 }}>
      <div className="container-wide">

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', color: 'var(--gold)', fontSize: '2.2rem' }}>
              Admin
            </h2>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: 4 }}>
              valid or mid — site management
            </p>
          </div>
          <Link to="/moderation">
            <button className="btn-primary" style={{ width: 'auto', padding: '10px 24px', position: 'relative' }}>
              Moderation queue
              {stats?.pendingUsers > 0 && (
                <span style={{
                  position: 'absolute', top: -8, right: -8,
                  background: 'var(--error)', color: 'white',
                  borderRadius: '50%', width: 20, height: 20,
                  fontSize: '0.7rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {stats.pendingUsers}
                </span>
              )}
            </button>
          </Link>
        </div>

        {/* Stats */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 32 }}>
            {[
              { label: 'Total users',    value: stats.totalUsers,    color: 'var(--text)' },
              { label: 'Live profiles',  value: stats.approvedUsers, color: 'var(--valid-color)' },
              { label: 'Pending review', value: stats.pendingUsers,  color: 'var(--gold)' },
              { label: 'No photo yet',   value: stats.noPhoto,       color: 'var(--text-muted)' },
              { label: 'Total ratings',  value: stats.totalRatings,  color: 'var(--purple-light)' },
            ].map(({ label, value, color }) => (
              <div key={label} className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '2rem', color }}>{value}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="form-group" style={{ marginBottom: 20 }}>
          <input
            type="text"
            placeholder="Search by display name..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            style={{ maxWidth: 360 }}
          />
        </div>

        {/* User table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: 'var(--dark)', borderBottom: '1px solid var(--border)' }}>
                  {['Photo', 'Name', 'Age', 'Score', 'Ratings', 'Joined', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.06em', fontSize: '0.75rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No users found.</td></tr>
                ) : users.map((u, i) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    {/* Photo thumbnail */}
                    <td style={{ padding: '10px 16px' }}>
                      {u.profile_photo_url ? (
                        <img
                          src={u.profile_photo_url}
                          alt={u.display_name}
                          style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: 4, background: 'var(--mid-gray)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          None
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--text)', fontWeight: 600 }}>{u.display_name}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-muted)' }}>{getAge(u.date_of_birth)}</td>
                    <td style={{ padding: '10px 16px', color: u.current_score ? 'var(--gold)' : 'var(--text-muted)', fontFamily: 'var(--font-heading)', fontSize: '1.1rem' }}>
                      {u.current_score ? u.current_score.toFixed(1) : '—'}
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-muted)' }}>{u.total_ratings_received}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatDate(u.created_at)}</td>
                    <td style={{ padding: '10px 16px' }}>
                      {!u.profile_photo_url ? (
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', letterSpacing: '0.06em' }}>No photo</span>
                      ) : (
                        <span className={`badge ${u.photo_approved ? 'badge-live' : 'badge-pending'}`}>
                          {u.photo_approved ? 'Live' : 'Pending'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {u.profile_photo_url && (
                          <button
                            onClick={() => toggleApproval(u.id, u.photo_approved)}
                            style={{
                              padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer',
                              background: 'transparent', border: `1px solid ${u.photo_approved ? 'var(--error)' : 'var(--valid-color)'}`,
                              color: u.photo_approved ? 'var(--error)' : 'var(--valid-color)',
                              borderRadius: 4, fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
                            }}
                          >
                            {u.photo_approved ? 'Revoke' : 'Approve'}
                          </button>
                        )}
                        <button
                          onClick={() => deleteUser(u.id, u.display_name)}
                          style={{
                            padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer',
                            background: 'transparent', border: '1px solid var(--mid-gray)',
                            color: 'var(--text-muted)', borderRadius: 4, fontFamily: 'var(--font-body)',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} users
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0} style={{ padding: '6px 14px', fontSize: '0.8rem' }}>
                  Previous
                </button>
                <button className="btn-ghost" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total} style={{ padding: '6px 14px', fontSize: '0.8rem' }}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
